import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getContentStatus } from '../api/contentApi';
import { fetchAllTVs } from '../api/tvApi';
import { getActiveProjectId } from '../constants/projectConstants';
import { watchScreenStatuses } from '../services/screenStatusWatcher';
import ScreenTimeline from './ScreenTimeline';
import {
  Play,
  Clock,
  CalendarClock,
  CircleOff,
  Monitor,
  Video,
  Image as ImageIcon,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Eye,
} from 'lucide-react';

const STATUS_CONFIG = {
  playing: {
    label: 'Live Now',
    color: 'var(--color-success-500, #10b981)',
    bg: 'var(--color-success-50, #ecfdf5)',
    border: 'var(--color-success-100, #d1fae5)',
    Icon: Play,
  },
  scheduled: {
    label: 'Scheduled',
    color: 'var(--color-warning-600, #d97706)',
    bg: 'var(--color-warning-50, #fffbeb)',
    border: 'var(--color-warning-100, #fef3c7)',
    Icon: CalendarClock,
  },
  idle: {
    label: 'Off Air',
    color: 'var(--color-danger-500, #ef4444)',
    bg: 'var(--color-danger-50, #fef2f2)',
    border: 'var(--color-danger-100, #fee2e2)',
    Icon: CircleOff,
  },
  never_scheduled: {
    label: 'Never Scheduled',
    color: 'var(--gray-400, #a8a29e)',
    bg: 'var(--gray-50, #fafaf9)',
    border: 'var(--gray-200, #e7e5e4)',
    Icon: Eye,
  },
};

const formatSlotTime = (slot) => {
  if (!slot) return '';
  const time = `${slot.startTime || '?'} - ${slot.endTime || '?'}`;
  if (slot.date) {
    const d = new Date(slot.date);
    if (!isNaN(d.getTime())) {
      const today = new Date().toISOString().slice(0, 10);
      const slotDay = d.toISOString().slice(0, 10);
      if (slotDay === today) return `Today ${time}`;
      const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
      if (slotDay === tomorrow) return `Tomorrow ${time}`;
      return `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} ${time}`;
    }
  }
  return time;
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const MediaTypeIcon = ({ type, size = 16 }) => {
  if (type === 'video') return <Video size={size} />;
  return <ImageIcon size={size} />;
};

/** ─── Status pill ─── */
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.never_scheduled;
  const Icon = cfg.Icon;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      padding: '4px 10px',
      borderRadius: '999px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.02em',
      color: cfg.color,
      backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap',
    }}>
      <Icon size={12} strokeWidth={2.5} />
      {cfg.label}
    </span>
  );
};

/** ─── Summary strip — Screens Online indicator (display-only) ─── */
const SummaryStrip = ({ devices }) => {
  const onlineCount = (devices || []).filter(d => d?.status?.toLowerCase() === 'online').length;
  const totalDevices = (devices || []).length;

  return (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'stretch' }}>
      <div
        style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: '14px 18px', borderRadius: '12px',
          backgroundColor: 'var(--color-success-600, #059669)',
          minWidth: '140px', gap: '2px', cursor: 'default',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Monitor size={16} color="#fff" />
          <span style={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.1, color: '#fff' }}>
            {onlineCount}/{totalDevices}
          </span>
        </div>
        <div style={{
          fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.85)',
        }}>
          Screens Online
        </div>
      </div>
    </div>
  );
};

/** ─── Single content row ─── */
const ContentRow = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const isImage = item.mediaType === 'image';
  const isVideo = item.mediaType === 'video';

  return (
    <div style={{
      backgroundColor: 'var(--surface-raised, #fff)',
      border: '1px solid var(--border-default, #e7e5e4)',
      borderRadius: '12px',
      overflow: 'hidden',
      transition: 'box-shadow 0.15s ease',
    }}>
      {/* Main row */}
      <div
        className="mobile-wrap"
        style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          padding: '16px 20px', cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Thumbnail */}
        <div style={{
          width: '56px', height: '56px', borderRadius: '8px', overflow: 'hidden',
          backgroundColor: 'var(--gray-100)', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {item.thumbnailUrl ? (
            isVideo ? (
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <img
                  src={item.thumbnailUrl}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.25)',
                }}>
                  <Play size={18} color="#fff" fill="#fff" />
                </div>
              </div>
            ) : (
              <img
                src={item.thumbnailUrl}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )
          ) : (
            <MediaTypeIcon type={item.mediaType} size={24} />
          )}
        </div>

        {/* Title + section */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {item.title}
          </div>
          <div style={{
            fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <MediaTypeIcon type={item.mediaType} size={12} />
            <span style={{ textTransform: 'capitalize' }}>{item.section}</span>
          </div>
        </div>

        {/* Screentime */}
        {item.totalScreenTimeMinutes > 0 && (
          <div style={{ textAlign: 'center', minWidth: '80px', flexShrink: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>
              {item.totalScreenTimeMinutes >= 60
                ? `${Math.floor(item.totalScreenTimeMinutes / 60)}h ${item.totalScreenTimeMinutes % 60 ? `${Math.round(item.totalScreenTimeMinutes % 60)}m` : ''}`
                : `${Math.round(item.totalScreenTimeMinutes)}m`}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {item.totalScreens > 1 ? `${item.totalScreens} screens` : 'screentime'}
            </div>
          </div>
        )}

        {/* Status badge */}
        <StatusBadge status={item.status} />

        {/* Context info: what's happening */}
        <div className="content-row__context" style={{ minWidth: '200px', textAlign: 'right' }}>
          {item.status === 'playing' && item.playingOnScreens.length > 0 && (
            <div style={{ fontSize: '13px', color: 'var(--text-default)' }}>
              <span style={{ fontWeight: 600 }}>{item.playingOnScreens.length} screen{item.playingOnScreens.length > 1 ? 's' : ''}</span>
              {' '}
              <span style={{ color: 'var(--text-muted)' }}>
                {item.playingOnScreens.slice(0, 2).map(s => s.displayName).join(', ')}
                {item.playingOnScreens.length > 2 ? ` +${item.playingOnScreens.length - 2}` : ''}
              </span>
            </div>
          )}
          {item.status === 'scheduled' && item.upcoming && (
            <div style={{ fontSize: '13px', color: 'var(--text-default)' }}>
              <Clock size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {formatSlotTime(item.upcoming)}
            </div>
          )}
          {item.status === 'idle' && item.lastPlayed && (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Last played: {item.lastPlayed.date ? new Date(item.lastPlayed.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'unknown'}
            </div>
          )}
          {item.status === 'never_scheduled' && item.uploadedAt && (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Uploaded {formatTimeAgo(item.uploadedAt)}
            </div>
          )}
        </div>

        {/* Expand toggle */}
        <div style={{ flexShrink: 0, color: 'var(--text-subtle)' }}>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div
          className="content-row__expanded"
          style={{
            padding: '0 20px 16px 92px', /* align with text after thumbnail */
            borderTop: '1px solid var(--border-subtle, #ececea)',
            paddingTop: '12px',
            display: 'flex', gap: '32px', flexWrap: 'wrap',
          }}
        >
          {/* Screens playing on */}
          {item.playingOnScreens.length > 0 && (
            <div>
              <div style={detailLabelStyle}>Playing on</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {item.playingOnScreens.map((s) => (
                  <div key={s.screenId} style={{ fontSize: '13px', color: 'var(--text-default)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Monitor size={13} color="var(--color-success-500)" />
                    {s.displayName}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming schedule */}
          {item.upcoming && (
            <div>
              <div style={detailLabelStyle}>Next scheduled</div>
              <div style={{ fontSize: '13px', color: 'var(--text-default)' }}>
                {formatSlotTime(item.upcoming)}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                Screen: {item.upcoming.screenId}
                {item.upcoming.isLoop ? ' (loop)' : ''}
              </div>
            </div>
          )}

          {/* Last played */}
          {item.lastPlayed && (
            <div>
              <div style={detailLabelStyle}>Last played</div>
              <div style={{ fontSize: '13px', color: 'var(--text-default)' }}>
                {formatSlotTime(item.lastPlayed)}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                Screen: {item.lastPlayed.screenId}
              </div>
            </div>
          )}

          {/* Expiry */}
          {item.validUntil && (
            <div>
              <div style={detailLabelStyle}>Expires</div>
              <div style={{ fontSize: '13px', color: new Date(item.validUntil) < new Date() ? 'var(--color-danger-500)' : 'var(--text-default)' }}>
                {new Date(item.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const detailLabelStyle = {
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  marginBottom: '4px',
};

/** ─── Main Component ─── */
const ContentDashboard = () => {
  const [contentData, setContentData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' | 'content'
  const [statusFilter, setStatusFilter] = useState(null);
  const [sectionFilter, setSectionFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statusRes, devicesRes] = await Promise.all([
        getContentStatus(getActiveProjectId()),
        fetchAllTVs(getActiveProjectId()).catch(() => ({ devices: [] })),
      ]);
      setContentData(statusRes);
      const list = Array.isArray(devicesRes?.devices) ? devicesRes.devices : Array.isArray(devicesRes) ? devicesRes : [];
      setDevices(list);
      // Reuse this already-fetched list to raise offline/online notifications
      // (no extra request). Shares one status map with the socket path.
      watchScreenStatuses(list);
    } catch (err) {
      setError(err.message || 'Failed to load content status');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // 15s poll so stat cards catch slot boundaries quickly (schedule
    // ending at :45 was still counted as "live" for up to 60s under the
    // old cadence, which made the dashboard look stale).
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Socket push: refetch immediately when a schedule intent is created,
  // edited, or cancelled. The 15s poll is the floor; this is the ceiling.
  useEffect(() => {
    let cancelled = false;
    // dynamic import so this file doesn't pull in the socket singleton
    // at module-eval time for consumers that don't need it.
    import('../sockets/dashboardSocket').then((mod) => {
      if (cancelled) return;
      const ds = mod.default;
      ds.connect();
      const handler = () => { loadData(); };
      ds.onIntentChanged(handler);
      return () => ds.offIntentChanged(handler);
    });
    return () => { cancelled = true; };
  }, [loadData]);

  const summary = contentData?.summary || { total: 0, playing: 0, scheduled: 0, idle: 0, neverScheduled: 0 };
  const allContent = contentData?.content || [];

  const sections = useMemo(() => {
    const set = new Set(allContent.map((c) => c.section).filter(Boolean));
    return Array.from(set).sort();
  }, [allContent]);

  const filteredContent = useMemo(() => {
    let items = allContent;
    if (statusFilter) {
      if (statusFilter === 'off_air') {
        items = items.filter((c) => c.status === 'idle' || c.status === 'never_scheduled');
      } else {
        items = items.filter((c) => c.status === statusFilter);
      }
    }
    if (sectionFilter) {
      items = items.filter((c) => c.section === sectionFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      items = items.filter((c) =>
        (c.title || '').toLowerCase().includes(q) ||
        (c.name || '').toLowerCase().includes(q) ||
        (c.contentId || '').toLowerCase().includes(q)
      );
    }
    return items;
  }, [allContent, statusFilter, sectionFilter, searchQuery]);

  if (isLoading && !contentData) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px', color: 'var(--text-muted)' }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '12px' }} />
        Loading content status...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Error banner */}
      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: '10px',
          backgroundColor: 'var(--color-danger-50)', border: '1px solid var(--color-danger-100)',
          color: 'var(--color-danger-700)', fontSize: '13px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{error}</span>
          <button onClick={loadData} style={{
            border: 'none', background: 'none', cursor: 'pointer',
            color: 'var(--color-danger-600)', fontWeight: 600, fontSize: '13px',
          }}>Retry</button>
        </div>
      )}

      {/* Card strip — Screens Online indicator only */}
      <SummaryStrip devices={devices} />

      {/* Below the cards: either timeline or content list — not both */}
      {viewMode === 'timeline' ? (
        <ScreenTimeline devices={devices} contentItems={allContent} />
      ) : (
        <>
          {/* Filter bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
          }}>
            <input
              type="text"
              placeholder="Search content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: '1 1 220px', minWidth: '180px',
                padding: '10px 14px', borderRadius: '8px',
                border: '1px solid var(--border-default, #e7e5e4)',
                fontSize: '13px', outline: 'none',
                backgroundColor: 'var(--surface-raised, #fff)',
              }}
            />
            <select
              value={sectionFilter || ''}
              onChange={(e) => setSectionFilter(e.target.value || null)}
              style={{
                padding: '10px 14px', borderRadius: '8px',
                border: '1px solid var(--border-default, #e7e5e4)',
                fontSize: '13px', cursor: 'pointer',
                backgroundColor: 'var(--surface-raised, #fff)',
                color: 'var(--text-default)',
              }}
            >
              <option value="">All sections</option>
              {sections.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <button
              onClick={loadData}
              disabled={isLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 16px', borderRadius: '8px', border: 'none',
                backgroundColor: 'var(--gold-500, #c8a147)', color: '#fff',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              <RefreshCw size={14} style={isLoading ? { animation: 'spin 1s linear infinite' } : undefined} />
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: 'auto' }}>
              {filteredContent.length} of {allContent.length} items
            </span>
          </div>

          {/* Content list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredContent.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '60px 20px',
                color: 'var(--text-muted)', fontSize: '14px',
                backgroundColor: 'var(--surface-raised)', borderRadius: '12px',
                border: '1px solid var(--border-default)',
              }}>
                {allContent.length === 0
                  ? 'No content uploaded yet. Go to Media to upload your first asset.'
                  : 'No content matches your filters.'}
              </div>
            ) : (
              filteredContent.map((item) => (
                <ContentRow key={item.contentId} item={item} />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ContentDashboard;
